// frontend/src/pages/SettingsPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import useApi from '../hooks/useApi';
import { 
    Calendar as CalendarIcon, 
    CreditCard, 
    KeyRound, 
    Users, 
    ChevronDown, 
    Edit, 
    Trash2, 
    X, 
    Star, 
    Shield, 
    Lock,
    Save,
    Sliders,
    ShieldAlert,
    Globe // Importado para o ícone de acesso global/cliente
} from 'lucide-react';

// --- HELPER PARA MÁSCARA DE CPF ---
const formatCPF = (value) => {
    let v = value.replace(/\D/g, ''); // Remove tudo o que não é dígito
    if (v.length > 11) v = v.slice(0, 11); // Limita a 11 dígitos
    v = v.replace(/(\d{3})(\d)/, '$1.$2'); // Coloca um ponto entre o terceiro e o quarto dígitos
    v = v.replace(/(\d{3})(\d)/, '$1.$2'); // Coloca um ponto entre o terceiro e o quarto dígitos de novo
    v = v.replace(/(\d{3})(\d{1,2})$/, '$1-$2'); // Coloca um hífen entre o terceiro e o quarto dígitos
    return v;
};

// --- NOVA CONFIGURAÇÃO: GRUPOS DE PERMISSÕES (CRUD) ---
const CRUD_ACTIONS = [
    { key: 'view', label: 'Visualizar' },
    { key: 'create', label: 'Incluir' },
    { key: 'edit', label: 'Editar' },
    { key: 'delete', label: 'Excluir' }
];

const PERMISSIONS_GROUPS = [
    {
        title: 'Visão Geral',
        items: [
            { baseKey: 'dashboard', label: 'Ver Dashboard', type: 'single', allowedTypes: ['all'] }
        ]
    },
    {
        title: 'Cadastros',
        items: [
            { baseKey: 'clients', label: 'Clientes / Fornecedores', type: 'crud', allowedTypes: ['all'] },
            { baseKey: 'commissions', label: 'Comissões', type: 'crud', allowedTypes: ['all'] }, // NOVO MÓDULO ADICIONADO
            { baseKey: 'service_table', label: 'Tabela de Serviços', type: 'crud', allowedTypes: ['energia'] },
            { baseKey: 'product_classifications', label: 'Classificação de Produtos', type: 'crud', allowedTypes: ['all'] },
            { baseKey: 'products', label: 'Produtos', type: 'crud', allowedTypes: ['all'] }
        ]
    },
    {
        title: 'Módulo Comercial',
        items: [
            { baseKey: 'comercial_status_view', label: 'Status Geral (Acompanhamento)', type: 'single', allowedTypes: ['energia'] },
            { baseKey: 'comercial_os', label: 'Ordens de Serviço / NSA (Acesso)', type: 'crud', allowedTypes: ['energia'] },
            { baseKey: 'comercial_os_create_btn', label: 'Botão: Criar Nova OS/NSA (Lista)', type: 'single', allowedTypes: ['energia'] },
            { baseKey: 'comercial_os_faturar_btn', label: 'Botão: Faturar (Aba Geral)', type: 'single', allowedTypes: ['energia'] },
            { baseKey: 'comercial_os_liberar_faturamento_btn', label: 'Botão: Liberar Faturamento (Aba Laudos Técnicos)', type: 'single', allowedTypes: ['energia'] },
            { baseKey: 'comercial_os_print_os', label: 'Botão: Imprimir OS/NSA', type: 'single', allowedTypes: ['energia'] },
            { baseKey: 'comercial_os_print_laudo', label: 'Botão: Imprimir Laudos Técnicos', type: 'single', allowedTypes: ['energia'] },
            { baseKey: 'comercial_os_hide_sensitive', label: 'Ocultar dados sensíveis (Valores)', type: 'single', allowedTypes: ['energia'] },
            { baseKey: 'comercial_schedule', label: 'Agendamentos', type: 'crud', allowedTypes: ['energia'] },
            
            { baseKey: 'laudos_vencidos_view', label: 'Controle de Laudos Vencidos', type: 'single', allowedTypes: ['energia'] },
            
            // ABAS
            { baseKey: 'os_tab_geral', label: 'Aba Geral (OS)', type: 'crud', allowedTypes: ['energia'] },
            { baseKey: 'os_tab_fotos', label: 'Aba Fotos (OS)', type: 'crud', allowedTypes: ['energia'] },
            { baseKey: 'os_tab_laudos_tecnicos', label: 'Aba Laudos Técnicos (OS)', type: 'crud', allowedTypes: ['energia'] },
        ]
    },
    {
        title: 'Módulo Financeiro',
        items: [
            { baseKey: 'transactions', label: 'Livro Caixa', type: 'crud', allowedTypes: ['all'] },
            { baseKey: 'payables', label: 'Contas a Pagar', type: 'crud', allowedTypes: ['all'] },
            { baseKey: 'receivables', label: 'Contas a Receber', type: 'crud', allowedTypes: ['all'] },
            { baseKey: 'accounts', label: 'Contas Bancárias', type: 'crud', allowedTypes: ['all'] },
        ]
    },
    {
        title: 'Módulo de Compras',
        items: [
            { baseKey: 'payment_conditions', label: 'Condições de Pagamento', type: 'crud', allowedTypes: ['all'] },
            { baseKey: 'supplier_negotiation', label: 'Negociação com Fornecedores', type: 'crud', allowedTypes: ['all'] },
            { baseKey: 'purchase_order', label: 'Pedidos de Compra', type: 'crud', allowedTypes: ['all'] },
            { baseKey: 'invoice_entry', label: 'Entrada de NF', type: 'crud', allowedTypes: ['all'] }
        ]
    },
    {
        title: 'Módulo de Estoque',
        items: [
            { baseKey: 'deposits', label: 'Cadastro Depósito', type: 'crud', allowedTypes: ['all'] },
            { baseKey: 'inventory_consult', label: 'Consulta Estoque', type: 'crud', allowedTypes: ['all'] },
            { baseKey: 'inventory_movements', label: 'Movimentações Depósitos', type: 'crud', allowedTypes: ['all'] },
            { baseKey: 'inventory_write_off', label: 'Baixas', type: 'crud', allowedTypes: ['all'] },
            { baseKey: 'inventory_audit', label: 'Inventário (Visão Completa e Divergências)', type: 'crud', allowedTypes: ['all'] },
            { baseKey: 'inventory_mobile_collect', label: 'Permitir Apenas Coleta Mobile', type: 'single', allowedTypes: ['all'] } // NOVA PERMISSÃO GRANULAR AQUI
        ]
    },
    {
        title: 'Módulo de Frota',
        items: [
            { baseKey: 'fleet_team', label: 'Cadastro de Equipe / Colaboradores', type: 'crud', allowedTypes: ['all'] },
            { baseKey: 'fleet_fuel', label: 'Controle de Combustível', type: 'crud', allowedTypes: ['all'] }
        ]
    },
    {
        title: 'Relatórios e Inteligência',
        items: [
            { baseKey: 'reports', label: 'Relatórios (Acesso Geral)', type: 'crud', allowedTypes: ['all'] }, 
            { baseKey: 'report_cash_book', label: 'Relatório: Livro Caixa', type: 'single', allowedTypes: ['all'] },
            { baseKey: 'report_ledger', label: 'Relatório: Extrato de Conta', type: 'single', allowedTypes: ['all'] },
            { baseKey: 'report_payables', label: 'Relatório: Contas a Pagar', type: 'single', allowedTypes: ['all'] },
            { baseKey: 'report_receivables', label: 'Relatório: Contas a Receber', type: 'single', allowedTypes: ['all'] },
            { baseKey: 'report_dre', label: 'Relatório: DRE', type: 'single', allowedTypes: ['all'] },
            { baseKey: 'report_services_client', label: 'Relatório: Serviços por Cliente', type: 'single', allowedTypes: ['all'] },
            { baseKey: 'assistant', label: 'Assistente IA', type: 'single', allowedTypes: ['all'] },
        ]
    },
    {
        title: 'Monitoramento de Atividade', 
        items: [
            { baseKey: 'monitoring_view', label: 'Visualizar Logs de Janelas Ativas', type: 'single', allowedTypes: ['all'] }
        ]
    },
    {
        title: 'Administração do Sistema',
        items: [
            { baseKey: 'settings', label: 'Configurações Gerais', type: 'crud', allowedTypes: ['all'] },
            { baseKey: 'settings_users', label: 'Utilizadores e Entidades', type: 'crud', allowedTypes: ['all'] },
            { baseKey: 'avisos', label: 'Avisos/Notificações', type: 'crud', allowedTypes: ['all'] },
            { baseKey: 'logs', label: 'Logs de Auditoria', type: 'single', allowedTypes: ['all'] }, 
            { baseKey: 'backup', label: 'Backup e Restauro', type: 'crud', allowedTypes: ['all'] }, 
            { baseKey: 'layout_docs', label: 'Layout Docs', type: 'crud', allowedTypes: ['energia'] }, 
            { baseKey: 'layout_laudos', label: 'Layout Laudos', type: 'crud', allowedTypes: ['energia'] }, 
        ]
    }
];

const GeneralSettingsManager = ({ settings, onDataChange }) => {
    const [avisoInterval, setAvisoInterval] = useState(settings.aviso_check_interval || 60);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const { apiFetch } = useApi();

    const handleSave = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage('');
        try {
            await apiFetch('/settings', {
                method: 'POST',
                body: { key: 'aviso_check_interval', value: avisoInterval },
            });
            setMessage('Configuração guardada com sucesso!');
            onDataChange();
        } catch (error) {
            setMessage(`Erro: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSave} className="space-y-4">
            <div>
                <label htmlFor="aviso-interval" className="block text-sm font-medium text-gray-700">
                    Intervalo de Verificação de Avisos (segundos)
                </label>
                <p className="text-xs text-gray-500 mb-2">Define a frequência com que o sistema procura por novos alertas em segundo plano.</p>
                <input 
                    type="number" 
                    id="aviso-interval"
                    value={avisoInterval}
                    onChange={(e) => setAvisoInterval(e.target.value)}
                    min="10"
                    placeholder="Ex: 60"
                    className="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
            </div>
            <button type="submit" disabled={loading} className="px-4 py-2 font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-blue-300">
                {loading ? 'A guardar...' : 'Guardar Configuração'}
            </button>
            {message && <p className="text-sm text-gray-600">{message}</p>}
        </form>
    );
};

const ApiKeysManager = ({ settings, onDataChange }) => {
    const [geminiApiKey, setGeminiApiKey] = useState(settings.geminiApiKey || '');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const { apiFetch } = useApi();

    const handleSave = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage('');
        try {
            await apiFetch('/settings', {
                method: 'POST',
                body: { key: 'geminiApiKey', value: geminiApiKey },
            });
            setMessage('Chave guardada com sucesso!');
            onDataChange();
        } catch (error) {
            setMessage(`Erro: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSave} className="space-y-4">
            <div>
                <label htmlFor="gemini-key" className="block text-sm font-medium text-gray-700">Chave da API do Gemini</label>
                <input 
                    type="password" 
                    id="gemini-key"
                    value={geminiApiKey}
                    onChange={(e) => setGeminiApiKey(e.target.value)}
                    placeholder="Cole a sua chave da API aqui"
                    className="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
            </div>
            <button type="submit" disabled={loading} className="px-4 py-2 font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-blue-300">
                {loading ? 'A guardar...' : 'Guardar Chave'}
            </button>
            {message && <p className="text-sm text-gray-600">{message}</p>}
        </form>
    );
};

const AccountTypesManager = ({ accountTypes, onDataChange }) => {
    const [editingType, setEditingType] = useState(null);
    const [newFieldName, setNewFieldName] = useState('');
    const { apiFetch } = useApi();

    useEffect(() => {
        setEditingType(null); 
    }, [accountTypes]);

    const handleSaveType = async () => {
        try {
            const payload = {
                ...editingType,
                fields: editingType.fields.map(f => ({
                    name: f.name,
                    debits: !!f.debits,
                    credits: !!f.credits,
                    rec_pg_fatura: !!f.rec_pg_fatura
                }))
            };

            if (editingType.id) {
                await apiFetch(`/account-types/${editingType.id}`, {
                    method: 'PUT',
                    body: payload,
                });
            } else {
                await apiFetch('/account-types', {
                    method: 'POST',
                    body: payload,
                });
            }
            setEditingType(null);
            onDataChange();
        } catch (error) {
            alert(`Erro ao guardar tipo: ${error.message}`);
        }
    };

    const handleDeleteType = async (typeId) => {
        if (window.confirm('Tem a certeza que quer excluir este tipo de conta?')) {
            try {
                await apiFetch(`/account-types/${typeId}`, { method: 'DELETE' });
                onDataChange();
            } catch (error) {
                alert(`Erro ao excluir tipo: ${error.message}`);
            }
        }
    };

    const handleAddField = () => {
        if (newFieldName && !editingType.fields.find(f => f.name === newFieldName)) {
            setEditingType(prev => ({ ...prev, fields: [...prev.fields, { name: newFieldName, debits: false, credits: false, rec_pg_fatura: false }] }));
            setNewFieldName('');
        }
    };

    const handleRemoveField = (fieldToRemove) => {
        setEditingType(prev => ({ ...prev, fields: prev.fields.filter(f => f.name !== fieldToRemove.name) }));
    };

    const handleFieldBehaviorChange = (fieldName, behavior, value) => {
        setEditingType(prev => ({
            ...prev,
            fields: prev.fields.map(f => f.name === fieldName ? { ...f, [behavior]: value } : f)
        }));
    };

    return (
        <div className="p-2 mt-2 space-y-2 border rounded">
            {editingType ? (
                <div className="p-2 mt-2 space-y-2 border rounded">
                    <div>
                        <label className="text-sm font-medium">Nome do Tipo</label>
                        <input type="text" value={editingType.name} onChange={(e) => setEditingType({ ...editingType, name: e.target.value })} className="w-full px-2 py-1 mt-1 border rounded-md"/>
                    </div>
                      <div>
                        <label className="text-sm font-medium">Status</label>
                        <select
                            value={editingType.status}
                            onChange={(e) => setEditingType({ ...editingType, status: e.target.value })}
                            className="w-full p-2 mt-1 border rounded"
                        >
                            <option value="ativo">Ativo</option>
                            <option value="inativo">Inativo</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-sm font-medium">Campos</label>
                        <div className="space-y-1">
                            {editingType.fields.map(field => (
                                <div key={field.name} className="p-2 bg-gray-100 rounded">
                                    <div className="flex items-center justify-between">
                                        <span>{field.name}</span>
                                        <button onClick={() => handleRemoveField(field)}><X size={16} /></button>
                                    </div>
                                    <div className="flex items-center gap-4 mt-2 text-xs">
                                            <label className="flex items-center gap-1">
                                                <input type="checkbox" checked={field.debits} onChange={(e) => handleFieldBehaviorChange(field.name, 'debits', e.target.checked)} />
                                                Debita
                                            </label>
                                            <label className="flex items-center gap-1">
                                                <input type="checkbox" checked={field.credits} onChange={(e) => handleFieldBehaviorChange(field.name, 'credits', e.target.checked)} />
                                                Credita
                                            </label>
                                            <label className="flex items-center gap-1 text-blue-600 font-semibold">
                                                <input type="checkbox" checked={field.rec_pg_fatura} onChange={(e) => handleFieldBehaviorChange(field.name, 'rec_pg_fatura', e.target.checked)} />
                                                Recebe Pag. Fatura
                                            </label>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-2 mt-2">
                            <input type="text" value={newFieldName} onChange={e => setNewFieldName(e.target.value)} placeholder="Novo campo" className="flex-grow px-2 py-1 border rounded-md"/>
                            <button type="button" onClick={handleAddField} className="px-3 py-1 text-white bg-gray-600 rounded-md">Adicionar</button>
                        </div>
                    </div>
                    <div className="flex gap-2 pt-2 mt-2 border-t">
                        <button onClick={handleSaveType} className="px-4 py-2 font-semibold text-white bg-blue-600 rounded-md">Guardar</button>
                        <button onClick={() => setEditingType(null)} className="px-4 py-2 font-semibold bg-gray-200 rounded-md">Cancelar</button>
                    </div>
                </div>
            ) : (
                <>
                    <button onClick={() => setEditingType({ name: '', fields: [], status: 'ativo' })} className="w-full px-4 py-2 mb-4 font-semibold text-white bg-green-600 rounded-md">Criar Novo Tipo de Conta</button>
                    <div className="space-y-2">
                        {accountTypes.map(type => (
                            <div key={type.id} className="flex items-center justify-between p-2 rounded hover:bg-gray-50">
                                <div>
                                    <span className={`font-semibold ${type.status === 'inativo' ? 'text-gray-400 line-through' : ''}`}>{type.name}</span>
                                    {type.status === 'inativo' && <span className="ml-2 text-xs text-red-500 bg-red-100 px-2 py-1 rounded-full">Inativo</span>}
                                </div>
                                <div className="flex items-center gap-2">
                                    <button onClick={() => setEditingType(type)} className="p-1 text-blue-600 hover:text-blue-800"><Edit size={20} /></button>
                                    <button onClick={() => handleDeleteType(type.id)} className="p-1 text-red-600 hover:text-red-800"><Trash2 size={20} /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

const CategoriesManager = ({ onDataChange }) => {
    const [categories, setCategories] = useState([]);
    const [editingCategory, setEditingCategory] = useState(null);
    const { apiFetch } = useApi();

    const fetchCategories = useCallback(async () => {
        try {
            const data = await apiFetch('/categories');
            setCategories(data);
        } catch (e) {
            console.error(e)
        }
    }, [apiFetch]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories, onDataChange]); 
    
    const handleSaveCategory = async () => {
        try {
            const payload = { ...editingCategory };

            if (editingCategory.id) {
                await apiFetch(`/categories/${editingCategory.id}`, { method: 'PUT', body: payload });
            } else {
                await apiFetch('/categories', { method: 'POST', body: payload });
            }
            setEditingCategory(null);
            fetchCategories();
            onDataChange();
        } catch (error) {
            alert(`Erro ao guardar categoria: ${error.message}`);
        }
    };

    const handleDeleteCategory = async (catId) => {
        if (window.confirm('Tem a certeza que quer excluir esta categoria?')) {
            try {
                await apiFetch(`/categories/${catId}`, { method: 'DELETE' });
                fetchCategories();
                onDataChange();
            } catch (error) {
                alert(`Erro ao excluir: ${error.message}`);
            }
        }
    };

    return (
        <div>
            {editingCategory ? (
                <div className="p-2 mt-2 space-y-2 border rounded">
                    <div>
                        <label className="text-sm font-medium">Nome da Categoria</label>
                        <input type="text" value={editingCategory.name} onChange={e => setEditingCategory({ ...editingCategory, name: e.target.value })} className="w-full px-2 py-1 mt-1 border rounded-md"/>
                    </div>
                    <div>
                        <label className="text-sm font-medium">Tipo</label>
                        <select value={editingCategory.type} onChange={e => setEditingCategory({ ...editingCategory, type: e.target.value })} className="w-full p-2 mt-1 border rounded">
                            <option value="expense">Despesa</option>
                            <option value="revenue">Receita</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-sm font-medium">Status</label>
                        <select value={editingCategory.status} onChange={e => setEditingCategory({ ...editingCategory, status: e.target.value })} className="w-full p-2 mt-1 border rounded">
                            <option value="ativo">Ativo</option>
                            <option value="inativo">Inativo</option>
                        </select>
                    </div>
                    <div className="flex gap-2 pt-2 mt-2 border-t">
                        <button onClick={handleSaveCategory} className="px-4 py-2 font-semibold text-white bg-blue-600 rounded-md">Guardar</button>
                        <button onClick={() => setEditingCategory(null)} className="px-4 py-2 font-semibold bg-gray-200 rounded-md">Cancelar</button>
                    </div>
                </div>
            ) : (
                <>
                      <button onClick={() => setEditingCategory({ name: '', type: 'expense', status: 'ativo' })} className="w-full px-4 py-2 mb-4 font-semibold text-white bg-green-600 rounded-md">
                        Criar Nova Categoria
                    </button>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                            <h3 className="font-semibold">Despesas</h3>
                            <div className="mt-2 space-y-1">
                                {categories.filter(c => c.type === 'expense').map(c => (
                                    <div key={c.id} className="flex items-center justify-between p-1 rounded hover:bg-gray-50">
                                                <div>
                                                    <span className={`${c.status === 'inativo' ? 'text-gray-400 line-through' : ''}`}>{c.name}</span>
                                                    {c.status === 'inativo' && <span className="ml-2 text-xs text-red-500 bg-red-100 px-2 py-1 rounded-full">Inativo</span>}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <button onClick={() => setEditingCategory(c)} className="p-1 text-blue-600 hover:text-blue-800"><Edit size={16}/></button>
                                                    <button onClick={() => handleDeleteCategory(c.id)} className="p-1 text-red-600 hover:text-red-800"><Trash2 size={16}/></button>
                                                </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div>
                            <h3 className="font-semibold">Receitas</h3>
                            <div className="mt-2 space-y-1">
                                {categories.filter(c => c.type === 'revenue').map(c => (
                                    <div key={c.id} className="flex items-center justify-between p-1 rounded hover:bg-gray-50">
                                                <div>
                                                    <span className={`${c.status === 'inativo' ? 'text-gray-400 line-through' : ''}`}>{c.name}</span>
                                                    {c.status === 'inativo' && <span className="ml-2 text-xs text-red-500 bg-red-100 px-2 py-1 rounded-full">Inativo</span>}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <button onClick={() => setEditingCategory(c)} className="p-1 text-blue-600 hover:text-blue-800"><Edit size={16}/></button>
                                                    <button onClick={() => handleDeleteCategory(c.id)} className="p-1 text-red-600 hover:text-red-800"><Trash2 size={16}/></button>
                                                </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

const CollapsibleSection = ({ title, icon: Icon, isOpen, onClick, children }) => {
    return (
        <div className="bg-white rounded-lg shadow">
            <button onClick={onClick} className="flex items-center justify-between w-full p-4 text-left">
                <div className="flex items-center gap-3">
                    <Icon className="text-blue-600" />
                    <h2 className="text-xl font-bold text-gray-800">{title}</h2>
                </div>
                <ChevronDown className={`transform transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            {isOpen && (
                <div className="p-4 border-t">
                    {children}
                </div>
            )}
        </div>
    );
};

const EntitiesManager = ({ onDataChange, onEditEntity, allCurrencies, currentUser }) => {
    const { apiFetch } = useApi();
    const [entities, setEntities] = useState([]);
    const [type, setType] = useState('CNPJ');
    const [identifier, setIdentifier] = useState('');
    const [name, setName] = useState('');
    const [logo, setLogo] = useState(null);
    const [defaultCurrency, setDefaultCurrency] = useState('BRL');
    const [businessType, setBusinessType] = useState('general');
    
    // FLAG MONITORAMENTO E LICENÇAS
    const [useMonitoring, setUseMonitoring] = useState(0);
    const [maxUsers, setMaxUsers] = useState(5);
    // NOVO: FLAG ACESSO CLIENTES
    const [allowClientAccess, setAllowClientAccess] = useState(0);

    const [country, setCountry] = useState('');
    const [stateRegistration, setStateRegistration] = useState('');
    const [addressStreet, setAddressStreet] = useState('');
    const [addressNumber, setAddressNumber] = useState('');
    const [addressComplement, setAddressComplement] = useState('');
    const [addressCity, setAddressCity] = useState('');
    const [addressState, setAddressState] = useState('');
    const [addressZip, setAddressZip] = useState('');

    const [loading, setLoading] = useState(true);
    const [formLoading, setFormLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const fetchEntities = useCallback(async () => {
        try {
            setLoading(true);
            const data = await apiFetch('/entities');
            setEntities(data);
        } catch (err) {
            console.error(err);
            setError('Falha ao carregar entidades.');
        } finally {
            setLoading(false);
        }
    }, [apiFetch]);

    useEffect(() => {
        fetchEntities();
    }, [fetchEntities, onDataChange]); 

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        if (name === 'type') setType(value);
        if (name === 'identifier') setIdentifier(value);
        if (name === 'name') setName(value);
        if (name === 'defaultCurrency') setDefaultCurrency(value);
        if (name === 'business_type') setBusinessType(value);
        if (name === 'country') setCountry(value);
        if (name === 'state_registration') setStateRegistration(value);
        if (name === 'address_street') setAddressStreet(value);
        if (name === 'address_number') setAddressNumber(value);
        if (name === 'address_complement') setAddressComplement(value);
        if (name === 'address_city') setAddressCity(value);
        if (name === 'address_state') setAddressState(value);
        if (name === 'address_zip') setAddressZip(value);
        if (name === 'max_users') setMaxUsers(value);
    };

    const handleFileChange = (e) => {
        setLogo(e.target.files[0] || null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setFormLoading(true);

        const formData = new FormData();
        formData.append('type', type);
        formData.append('identifier', identifier);
        formData.append('name', name);
        formData.append('defaultCurrency', defaultCurrency);
        formData.append('business_type', businessType);
        formData.append('use_monitoring', useMonitoring); 
        formData.append('max_users', maxUsers); 
        // NOVO: ENVIO ACESSO CLIENTES
        formData.append('allow_client_access', allowClientAccess);
        
        if (logo) {
            formData.append('logo', logo);
        }

        formData.append('country', country);
        formData.append('state_registration', stateRegistration);
        formData.append('address_street', addressStreet);
        formData.append('address_number', addressNumber);
        formData.append('address_complement', addressComplement);
        formData.append('address_city', addressCity);
        formData.append('address_state', addressState);
        formData.append('address_zip', addressZip);

        try {
            await apiFetch('/entities', {
                method: 'POST',
                body: formData
            });
            setSuccess(`Entidade "${name}" criada com sucesso!`);
            setType('CNPJ');
            setIdentifier('');
            setName('');
            setLogo(null);
            setDefaultCurrency('BRL');
            setBusinessType('general');
            setUseMonitoring(0);
            setAllowClientAccess(0);
            setMaxUsers(5);
            setCountry('');
            setStateRegistration('');
            setAddressStreet('');
            setAddressNumber('');
            setAddressComplement('');
            setAddressCity('');
            setAddressState('');
            setAddressZip('');
            e.target.reset(); 
            
            fetchEntities(); 
            if(onDataChange) onDataChange();
        } catch (err) {
            setError(err.message);
        } finally {
            setFormLoading(false);
        }
    };

    const handleDeleteEntity = async (entityId, entityName, entityIdentifier) => {
        if (entityIdentifier === '00000000000100') { 
            alert('A entidade padrão não pode ser excluída.');
            return;
        }
        if (window.confirm(`Tem a certeza que quer excluir a entidade "${entityName}"? Esta ação é irreversível e irá apagar todos os seus dados.`)) {
            try {
                await apiFetch(`/entities/${entityId}`, { method: 'DELETE' });
                setSuccess(`Entidade "${entityName}" excluída com sucesso.`);
                fetchEntities();
                if (onDataChange) onDataChange();
            } catch (err) {
                setError(err.message);
            }
        }
    };

    if (loading) return <p>A carregar entidades...</p>;

    return (
        <div className="space-y-4">
            <div>
                <h3 className="font-bold">Entidades Existentes</h3>
                 <div className="mt-2 space-y-2 max-h-48 overflow-y-auto pr-2">
                    {entities.map(entity => (
                        <div key={entity.id} className="flex items-center justify-between p-2 rounded bg-gray-50">
                             <div>
                                <p className={`font-semibold ${entity.status === 'inativo' ? 'text-gray-400 line-through' : ''}`}>{entity.name}</p>
                                <p className="text-sm text-gray-500">{entity.type}: {entity.identifier} | Licenças: {entity.max_users}</p>
                                {entity.status === 'inativo' && <span className="ml-2 text-xs text-red-500 bg-red-100 px-2 py-1 rounded-full">Inativo</span>}
                                {entity.use_monitoring === 1 && <span className="ml-2 text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">Monitorada</span>}
                                {entity.allow_client_access === 1 && <span className="ml-2 text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">Portal Cliente Ativo</span>}
                            </div>
                            <div className="flex items-center gap-2">
                                {/* TRAVA: Apenas 'admin' vê o botão editar entidade */}
                                {currentUser.username === 'admin' && (
                                    <button onClick={() => onEditEntity(entity)} className="p-1 text-blue-600 hover:text-blue-800"><Edit size={20} /></button>
                                )}
                                {/* TRAVA: Apenas 'admin' vê o botão excluir entidade */}
                                {currentUser.username === 'admin' && entity.identifier !== '00000000000100' && (
                                    <button onClick={() => handleDeleteEntity(entity.id, entity.name, entity.identifier)} className="p-1 text-red-600 hover:text-red-800"><Trash2 size={20} /></button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            
            {/* TRAVA: Apenas 'admin' vê o formulário de criar nova entidade */}
            {currentUser.username === 'admin' && (
                <form onSubmit={handleSubmit} className="p-4 mt-4 space-y-4 border rounded-lg bg-gray-50">
                    <h3 className="text-lg font-bold">Criar Nova Entidade</h3>
                    {error && <p className="text-sm text-red-600">{error}</p>}
                    {success && <p className="text-sm text-green-600">{success}</p>}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                            <label className="text-sm font-medium">Nome da Entidade</label>
                            <input type="text" name="name" value={name} onChange={handleFormChange} required className="w-full p-2 mt-1 border rounded" />
                        </div>
                        <div>
                            <label className="text-sm font-medium">Tipo</label>
                            <select name="type" value={type} onChange={handleFormChange} className="w-full p-2 mt-1 border rounded">
                                <option value="CNPJ">CNPJ</option>
                                <option value="CPF">CPF</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-sm font-medium">Moeda Padrão</label>
                            <select name="defaultCurrency" value={defaultCurrency} onChange={handleFormChange} required className="w-full p-2 mt-1 border rounded">
                                {allCurrencies.map(c => <option key={c.code} value={c.code}>{c.name} ({c.code})</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-sm font-medium">Tipo de Negócio</label>
                            <select name="business_type" value={businessType} onChange={handleFormChange} className="w-full p-2 mt-1 border rounded">
                                <option value="general">Geral</option>
                                <option value="pet">Pet</option>
                                <option value="energia">Energia</option>
                            </select>
                        </div>
                        
                        <div className="md:col-span-2">
                            <label className="text-sm font-medium">Limite de Usuários (Licenças)</label>
                            <input type="number" name="max_users" value={maxUsers} onChange={handleFormChange} min="1" required className="w-full p-2 mt-1 border rounded" />
                        </div>

                        {/* CAMPO MONITORAMENTO ENTIDADE */}
                        <div className="md:col-span-2 flex items-center gap-2 bg-blue-50 p-2 rounded border border-blue-200">
                             <input 
                                type="checkbox" 
                                id="ent_monitoring"
                                checked={useMonitoring === 1} 
                                onChange={(e) => setUseMonitoring(e.target.checked ? 1 : 0)}
                                className="w-4 h-4 text-blue-600 rounded"
                             />
                             <label htmlFor="ent_monitoring" className="text-sm font-bold text-blue-800 flex items-center gap-1">
                                <ShieldAlert size={16} /> Ativar monitoramento de PC para esta empresa
                             </label>
                        </div>

                        {/* NOVO: CAMPO PERMITIR ACESSO CLIENTES */}
                        <div className="md:col-span-2 flex items-center gap-2 bg-green-50 p-2 rounded border border-green-200">
                             <input 
                                type="checkbox" 
                                id="allow_client_access"
                                checked={allowClientAccess === 1} 
                                onChange={(e) => setAllowClientAccess(e.target.checked ? 1 : 0)}
                                className="w-4 h-4 text-green-600 rounded"
                             />
                             <label htmlFor="allow_client_access" className="text-sm font-bold text-green-800 flex items-center gap-1">
                                <Globe size={16} /> Permitir acesso clientes ao sistema
                             </label>
                        </div>

                        <div>
                            <label className="text-sm font-medium">País</label>
                            <input type="text" name="country" value={country} onChange={handleFormChange} className="w-full p-2 mt-1 border rounded" />
                        </div>
                          <div>
                            <label className="text-sm font-medium">Insc. Estadual</label>
                            <input type="text" name="state_registration" value={stateRegistration} onChange={handleFormChange} className="w-full p-2 mt-1 border rounded" />
                        </div>
                        <div className="md:col-span-2">
                            <label className="text-sm font-medium">Identificador (CNPJ/CPF)</label>
                            <input type="text" name="identifier" value={identifier} onChange={handleFormChange} required className="w-full p-2 mt-1 border rounded" />
                        </div>
                        <div className="md:col-span-2">
                            <label className="text-sm font-medium">Logomarca</label>
                            <input type="file" accept="image/png" onChange={handleFileChange} className="w-full p-2 mt-1 text-sm border rounded" />
                            <p className="text-xs text-gray-500 mt-1">PNG quadrado (ex: 256x256 pixels). Máx 2MB.</p>
                        </div>
                    </div>

                    <button type="submit" disabled={formLoading} className="px-4 py-2 font-semibold text-white bg-green-600 rounded-md">
                        {formLoading ? 'A criar...' : 'Criar Entidade'}
                    </button>
                </form>
            )}
        </div>
    );
}

const CreateUserForm = ({ roles, entities, onUserCreated, allCurrencies, currentUser }) => {
    const { apiFetch } = useApi();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [recoveryKeyword, setRecoveryKeyword] = useState('');
    const [roleId, setRoleId] = useState('');
    const [entityId, setEntityId] = useState('');
    const [allowedCurrencies, setAllowedCurrencies] = useState([]);
    const [isSuperUser, setIsSuperUser] = useState(false); 
    
    // --- NOVOS ESTADOS: DADOS PESSOAIS ---
    const [fullName, setFullName] = useState('');
    const [cpf, setCpf] = useState('');
    const [cnh, setCnh] = useState('');

    const [requiresMonitoring, setRequiresMonitoring] = useState(false);

    // --- NOVOS ESTADOS: ASSINATURA NA CRIAÇÃO ---
    const [signatureName, setSignatureName] = useState('');
    const [signatureRole, setSignatureRole] = useState('');
    const [signatureRegistry, setSignatureRegistry] = useState('');
    const [isSignaturePublic, setIsSignaturePublic] = useState(false);
    const [signatureImageBase64, setSignatureImageBase64] = useState('');

    // --- NOVO ESTADO: ENGENHEIRO RESPONSÁVEL ---
    const [isResponsibleEngineer, setIsResponsibleEngineer] = useState(false);

    // --- NOVO ESTADO: MULTI-EMPRESA ---
    const [allowedEntities, setAllowedEntities] = useState([]);

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (roles.length > 0 && !roleId) {
            setRoleId(roles.find(r => r.name === 'Padrão')?.id || roles[0].id);
        }
        if (entities.length > 0 && !entityId) {
            setEntityId(entities[0].id);
        }
    }, [roles, entities, roleId, entityId]);
    
    const handleCurrencyChange = (currencyCode) => {
        setAllowedCurrencies(prev => 
            prev.includes(currencyCode)
            ? prev.filter(c => c !== currencyCode)
            : [...prev, currencyCode]
        );
    };

    const handleAllowedEntityChange = (entId) => {
        setAllowedEntities(prev => 
            prev.includes(entId) ? prev.filter(id => id !== entId) : [...prev, entId]
        );
    };

    const handleSignatureFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setSignatureImageBase64(reader.result);
            };
            reader.readAsDataURL(file);
        } else {
            setSignatureImageBase64('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        if (!entityId) {
            setError('Por favor, selecione uma entidade. Se não houver, crie uma primeiro.');
            return;
        }
        if (allowedCurrencies.length === 0) {
            setError('Selecione pelo menos uma moeda permitida para o utilizador.');
            return;
        }

        setLoading(true);
        try {
            await apiFetch('/users', {
                method: 'POST',
                body: { 
                    username, 
                    password, 
                    roleId, 
                    recoveryKeyword, 
                    entityId, 
                    is_super_user: isSuperUser, 
                    allowedCurrencies,
                    requires_monitoring: requiresMonitoring,
                    
                    full_name: fullName,
                    cpf: cpf,
                    cnh: cnh,

                    signature_name: signatureName,
                    signature_role: signatureRole,
                    signature_registry: signatureRegistry,
                    is_signature_public: isSignaturePublic,
                    signature_image_base64: signatureImageBase64,
                    is_responsible_engineer: isResponsibleEngineer,
                    allowedEntities: isSuperUser ? allowedEntities : []
                }
            });
            setSuccess(`Utilizador "${username}" criado com sucesso!`);
            setUsername('');
            setPassword('');
            setRecoveryKeyword('');
            setAllowedCurrencies([]);
            setIsSuperUser(false);
            setRequiresMonitoring(false);
            setAllowedEntities([]);
            
            setFullName('');
            setCpf('');
            setCnh('');

            // Limpa os campos de assinatura e engenheiro
            setSignatureName('');
            setSignatureRole('');
            setSignatureRegistry('');
            setIsSignaturePublic(false);
            setSignatureImageBase64('');
            setIsResponsibleEngineer(false);
            const fileInput = document.getElementById('create_signature_file');
            if(fileInput) fileInput.value = '';

            onUserCreated(); 
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }

    return (
        <form onSubmit={handleSubmit} className="p-4 mt-4 space-y-4 border rounded-lg bg-gray-50">
            <h3 className="text-lg font-bold">Criar Novo Utilizador</h3>
            {error && <p className="text-sm text-red-600">{error}</p>}
            {success && <p className="text-sm text-green-600">{success}</p>}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                 <div>
                    <label className="text-sm font-medium">Nome de Utilizador (Login)</label>
                    <input type="text" value={username} onChange={e => setUsername(e.target.value)} required className="w-full p-2 mt-1 border rounded"/>
                </div>
                 <div>
                    <label className="text-sm font-medium">Senha Inicial</label>
                    <input type="password" value={password} onChange={e => setPassword(e.target.value)} required className="w-full p-2 mt-1 border rounded"/>
                </div>

                {/* --- NOVOS CAMPOS PESSOAIS --- */}
                <div className="md:col-span-2">
                    <label className="text-sm font-medium">Nome Completo</label>
                    <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} className="w-full p-2 mt-1 border rounded" placeholder="Nome real do usuário" />
                </div>
                <div>
                    <label className="text-sm font-medium">CPF</label>
                    <input 
                        type="text" 
                        value={cpf} 
                        onChange={e => setCpf(formatCPF(e.target.value))} 
                        className="w-full p-2 mt-1 border rounded" 
                        placeholder="000.000.000-00" 
                    />
                </div>
                <div>
                    <label className="text-sm font-medium">CNH (Opcional)</label>
                    <input type="text" value={cnh} onChange={e => setCnh(e.target.value)} className="w-full p-2 mt-1 border rounded" placeholder="Número da CNH" />
                </div>
                {/* ----------------------------- */}

                 <div>
                    <label className="text-sm font-medium">Palavra-Chave de Recuperação</label>
                    <input type="text" value={recoveryKeyword} onChange={e => setRecoveryKeyword(e.target.value)} required className="w-full p-2 mt-1 border rounded"/>
                </div>
                <div>
                    <label className="text-sm font-medium">Função</label>
                    <select value={roleId} onChange={e => setRoleId(e.target.value)} className="w-full p-2 mt-1 border rounded">
                        {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                </div>
                 <div className="md:col-span-2">
                    <label className="text-sm font-medium">Entidade</label>
                    <select value={entityId} onChange={e => setEntityId(e.target.value)} className="w-full p-2 mt-1 border rounded">
                        <option value="">Selecione a Entidade</option>
                        {entities.map(e => <option key={e.id} value={e.id}>{e.name} ({e.identifier})</option>)}
                    </select>
                </div>
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Moedas Permitidas</label>
                    <div className="flex flex-wrap gap-4 p-3 mt-1 border rounded-md bg-white">
                        {allCurrencies.map(currency => (
                            <label key={currency.code} className="flex items-center gap-2">
                                <input 
                                    type="checkbox"
                                    checked={allowedCurrencies.includes(currency.code)}
                                    onChange={() => handleCurrencyChange(currency.code)}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <span>{currency.name} ({currency.code})</span>
                            </label>
                        ))}
                    </div>
                </div>

                {!isSuperUser && (
                    <div className="flex items-center gap-2 md:col-span-2 p-2 bg-blue-50 border border-blue-100 rounded">
                        <input 
                            id="requires_monitoring"
                            type="checkbox"
                            checked={requiresMonitoring}
                            onChange={(e) => setRequiresMonitoring(e.target.checked)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <label htmlFor="requires_monitoring" className="text-sm font-bold text-blue-700 flex items-center gap-1">
                            <ShieldAlert size={16} /> Exigir monitoramento de PC ativo para este utilizador
                        </label>
                    </div>
                )}

                {currentUser.is_super_user && (
                    <div className="flex items-center gap-2 md:col-span-2">
                        <input
                            id="is_super_user"
                            type="checkbox"
                            checked={isSuperUser}
                            onChange={(e) => {
                                setIsSuperUser(e.target.checked);
                                if(e.target.checked) setRequiresMonitoring(false); 
                            }}
                            className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-400"
                        />
                        <label htmlFor="is_super_user" className="flex items-center gap-1 text-sm font-medium text-gray-700">
                            <Star size={16} className="text-yellow-500"/>
                            É um Super Usuário?
                        </label>
                    </div>
                )}

                {/* --- NOVO: CAIXA MULTI-EMPRESA --- */}
                {isSuperUser && (
                    <div className="md:col-span-2 mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded">
                        <label className="block text-sm font-bold text-yellow-800 mb-2">Acesso Multi-empresa (Licenças Consumidas)</label>
                        <div className="flex flex-wrap gap-4">
                            {entities.map(ent => (
                                <label key={ent.id} className="flex items-center gap-2">
                                    <input 
                                        type="checkbox"
                                        checked={allowedEntities.includes(ent.id) || String(entityId) === String(ent.id)}
                                        onChange={() => handleAllowedEntityChange(ent.id)}
                                        disabled={String(entityId) === String(ent.id)}
                                        className="w-4 h-4 text-yellow-600 rounded focus:ring-yellow-500"
                                    />
                                    <span className="text-sm text-yellow-900">{ent.name}</span>
                                </label>
                            ))}
                        </div>
                        <p className="text-xs text-yellow-700 mt-2 font-bold">Atenção: O super usuário consumirá 1 licença em cada entidade selecionada acima.</p>
                    </div>
                )}

                {/* --- NOVA SEÇÃO: ASSINATURA DO TÉCNICO (CRIAÇÃO) --- */}
                <div className="md:col-span-2 pt-4 border-t mt-2">
                    <h4 className="text-md font-bold mb-3 text-gray-700">Assinatura do Técnico (Laudos Tensão Aplicada)</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded border">
                        <div>
                            <label className="text-sm font-medium">Nome na Assinatura</label>
                            <input type="text" value={signatureName} onChange={e => setSignatureName(e.target.value)} placeholder="Ex: Bruno Braga De Souza" className="w-full p-2 mt-1 border rounded bg-white" />
                        </div>
                        <div>
                            <label className="text-sm font-medium">Função</label>
                            <input type="text" value={signatureRole} onChange={e => setSignatureRole(e.target.value)} placeholder="Ex: Técnico em Eletrotécnica" className="w-full p-2 mt-1 border rounded bg-white" />
                        </div>
                        <div>
                            <label className="text-sm font-medium">Registro (CREA/CRT)</label>
                            <input type="text" value={signatureRegistry} onChange={e => setSignatureRegistry(e.target.value)} placeholder="Ex: CRT - SP nº 15444951703" className="w-full p-2 mt-1 border rounded bg-white" />
                        </div>
                        
                        <div className="flex flex-col gap-2 mt-4">
                            <div className="flex items-center gap-2">
                                <input 
                                    id="create_is_signature_public"
                                    type="checkbox"
                                    checked={isSignaturePublic}
                                    onChange={(e) => setIsSignaturePublic(e.target.checked)}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor="create_is_signature_public" className="text-sm font-bold text-gray-700 cursor-pointer">
                                    Assinatura Pública (Outros podem usar)
                                </label>
                            </div>

                            {/* --- NOVO CHECKBOX: ENGENHEIRO RESPONSÁVEL --- */}
                            <div className="flex items-center gap-2">
                                <input 
                                    id="create_is_responsible_engineer"
                                    type="checkbox"
                                    checked={isResponsibleEngineer}
                                    onChange={(e) => setIsResponsibleEngineer(e.target.checked)}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor="create_is_responsible_engineer" className="text-sm font-bold text-gray-700 cursor-pointer">
                                    Engenheiro Responsável
                                </label>
                            </div>
                        </div>

                        <div className="md:col-span-2">
                            <label className="text-sm font-medium">Imagem da Assinatura (Fundo Transparente Recomendado)</label>
                            <input type="file" id="create_signature_file" accept="image/png, image/jpeg" onChange={handleSignatureFileChange} className="w-full p-2 mt-1 text-sm border rounded bg-white" />
                            {signatureImageBase64 && (
                                <div className="mt-3 p-3 border rounded bg-white flex flex-col items-center shadow-sm">
                                    <span className="text-xs text-gray-500 mb-2 font-bold uppercase tracking-wider">Pré-visualização da Assinatura:</span>
                                    <img src={signatureImageBase64} alt="Assinatura" style={{ maxHeight: '80px', objectFit: 'contain' }} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {/* ---------------------------------------- */}

            </div>
             <button type="submit" disabled={loading} className="px-4 py-2 font-semibold text-white bg-green-600 rounded-md">
                {loading ? 'A criar...' : 'Criar Utilizador'}
            </button>
        </form>
    )
}

const UsersManager = ({ onDataChange, onEditUser, currentUser, allCurrencies }) => {
    const { apiFetch } = useApi();
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [entities, setEntities] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchData = useCallback(async () => {
        if (!currentUser) {
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            const usersUrl = '/users';
            const [usersData, rolesData, entitiesData] = await Promise.all([
                apiFetch(usersUrl),
                apiFetch('/roles'),
                apiFetch('/entities')
            ]);
            setUsers(usersData);
            setRoles(rolesData);
            setEntities(entitiesData);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [apiFetch, currentUser]);

    useEffect(() => {
        fetchData();
    }, [fetchData, onDataChange]); 
    
    const handleDeleteUser = async (userId, username) => {
        if (username === 'admin') {
            alert('Não é possível excluir o utilizador "admin".');
            return;
        }
        if (window.confirm(`Tem a certeza que quer excluir o utilizador "${username}"?`)) {
            try {
                await apiFetch(`/users/${userId}`, { method: 'DELETE' });
                fetchData();
            } catch (err) {
                alert(err.message);
            }
        }
    }
    
    if (loading) return <p>A carregar...</p>;

    return (
        <div className="space-y-4">
            <div>
                <h3 className="font-bold">Utilizadores Existentes</h3>
                 <div className="mt-2 space-y-2 max-h-48 overflow-y-auto pr-2">
                    {users.map(user => (
                        <div key={user.id} className="grid grid-cols-3 items-center gap-4 p-2 rounded hover:bg-gray-50">
                            <div>
                                <p className={`font-semibold ${user.status === 'inativo' ? 'text-gray-400 line-through' : ''}`}>
                                    {user.username}
                                    {user.is_super_user && <span className="ml-2 text-xs font-bold text-yellow-800 bg-yellow-200 px-2 py-0.5 rounded-full">SUPER</span>}
                                    {user.requires_monitoring === 1 && <ShieldAlert size={14} className="inline ml-2 text-blue-600" title="Monitoramento Ativo"/>}
                                </p>
                                <p className="text-sm text-gray-500">{user.entityName}</p>
                                {user.status === 'inativo' && <span className="ml-2 text-xs text-red-500 bg-red-100 px-2 py-1 rounded-full">Inativo</span>}
                            </div>
                            <p className="text-sm text-gray-600">{user.roleName}</p>
                            <div className="text-right">
                                {/* TRAVA DE SEGURANÇA: Só o próprio usuário 'admin' pode editar o 'admin'. Outros super usuários podem editar os demais. */}
                                {(user.username !== 'admin' || currentUser.username === 'admin') && (
                                     <button onClick={() => onEditUser(user.id)} className="p-1 text-blue-600 hover:text-blue-800" title="Editar"><Edit size={20}/></button>
                                )}
                                {/* TRAVA DE SEGURANÇA: Ninguém pode excluir o usuário 'admin' */}
                                {user.username !== 'admin' && (
                                     <button onClick={() => handleDeleteUser(user.id, user.username)} className="p-1 text-red-600 hover:text-red-800" title="Excluir"><Trash2 size={20}/></button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <CreateUserForm roles={roles} entities={entities} onUserCreated={fetchData} allCurrencies={allCurrencies} currentUser={currentUser} />
        </div>
    );
};

// --- COMPONENTE ROLES MANAGER ATUALIZADO (COM CRUD) ---
const RolesManager = ({ onDataChange, currentUser }) => {
    const { apiFetch } = useApi();
    const [roles, setRoles] = useState([]);
    const [editingRole, setEditingRole] = useState(null);
    const [loading, setLoading] = useState(true);

    const availableGroups = useMemo(() => {
        return PERMISSIONS_GROUPS.map(group => ({
            ...group,
            items: group.items.filter(p => 
                p.allowedTypes.includes('all') || 
                (currentUser && p.allowedTypes.includes(currentUser.business_type))
            )
        })).filter(group => group.items.length > 0);
    }, [currentUser]);

    const fetchRoles = useCallback(async () => {
        try {
            setLoading(true);
            const data = await apiFetch('/roles');
            setRoles(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [apiFetch]);

    useEffect(() => {
        fetchRoles();
    }, [fetchRoles, onDataChange]); 

    const handleEdit = (role) => {
        let perms = [];
        try {
            if (typeof role.permissions === 'string') {
                perms = JSON.parse(role.permissions);
            } else if (Array.isArray(role.permissions)) {
                perms = role.permissions;
            }
        } catch (e) { perms = []; }

        setEditingRole({
            ...role,
            permissions: perms 
        });
    };

    const handleCancel = () => {
        setEditingRole(null);
    };

    const handleSave = async () => {
        if (!editingRole.name.trim()) {
            alert("O nome da função é obrigatório");
            return;
        }
        try {
            const payload = {
                name: editingRole.name,
                permissions: editingRole.permissions 
            };

            if (editingRole.id) {
                await apiFetch(`/roles/${editingRole.id}`, { method: 'PUT', body: payload });
            } else {
                await apiFetch('/roles', { method: 'POST', body: payload });
            }
            setEditingRole(null);
            fetchRoles();
            if (onDataChange) onDataChange();
        } catch(err) {
            alert(`Erro ao guardar função: ${err.message}`);
        }
    };

    const handleDelete = async (roleId, roleName) => {
        if (['Admin', 'Padrão'].includes(roleName)) {
            alert('Não é possível excluir as funções padrão.');
            return;
        }
        if (window.confirm('Tem a certeza?')) {
            try {
                 await apiFetch(`/roles/${roleId}`, { method: 'DELETE' });
                 fetchRoles();
                 if (onDataChange) onDataChange();
            } catch (error) {
                alert(error.message);
            }
        }
    };

    const togglePermission = (permKey) => {
        setEditingRole(prev => {
            const hasPerm = prev.permissions.includes(permKey);
            let newPerms;
            if (hasPerm) {
                newPerms = prev.permissions.filter(p => p !== permKey);
            } else {
                newPerms = [...prev.permissions, permKey];
            }
            return { ...prev, permissions: newPerms };
        });
    };

    const toggleAllInModule = (baseKey) => {
        setEditingRole(prev => {
            const allActions = CRUD_ACTIONS.map(action => `${baseKey}_${action.key}`);
            const hasAll = allActions.every(key => prev.permissions.includes(key));

            let newPerms = [...prev.permissions];
            
            if (hasAll) {
                newPerms = newPerms.filter(p => !allActions.includes(p));
            } else {
                allActions.forEach(key => {
                    if (!newPerms.includes(key)) newPerms.push(key);
                });
            }
            return { ...prev, permissions: newPerms };
        });
    };
    
    if (loading) return <p>A carregar funções...</p>;

    if (editingRole) {
        return (
             <div className="p-6 space-y-6 border rounded-lg bg-white shadow-sm">
                <h3 className="text-xl font-bold text-gray-800 border-b pb-2">
                    {editingRole.id ? `Editar Função: ${editingRole.name}` : 'Nova Função'}
                </h3>
                
                <div>
                    <label className="text-sm font-medium text-gray-700 block mb-1">Nome da Função</label>
                    <input 
                        type="text" 
                        value={editingRole.name} 
                        onChange={e => setEditingRole({...editingRole, name: e.target.value})} 
                        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                        placeholder="Ex: Assistente Financeiro"
                    />
                </div>

                <div className="space-y-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <p className="text-sm text-blue-600 bg-blue-50 p-2 rounded border border-blue-200 mb-4">
                        <strong>Dica:</strong> Clique no nome do módulo para selecionar/desmarcar todas as ações.
                    </p>

                    {availableGroups.map((group, index) => (
                        <div key={index} className="mb-6 last:mb-0">
                            <h4 className="text-md font-bold text-gray-700 mb-3 flex items-center gap-2 border-b pb-1">
                                <Shield size={16} /> {group.title}
                            </h4>
                            
                            {group.items.some(i => i.type === 'crud') && (
                                <div className="hidden md:grid grid-cols-12 gap-4 mb-2 text-xs font-bold text-gray-500 uppercase text-center">
                                    <div className="col-span-4 text-left pl-2">Módulo</div>
                                    <div className="col-span-2">Ver</div>
                                    <div className="col-span-2">Criar</div>
                                    <div className="col-span-2">Editar</div>
                                    <div className="col-span-2">Excluir</div>
                                </div>
                            )}

                            <div className="space-y-2">
                                {group.items.map(item => {
                                    if (item.type === 'crud') {
                                        return (
                                            <div key={item.baseKey} className="md:grid md:grid-cols-12 md:gap-4 items-center bg-white p-2 rounded border border-gray-100 hover:shadow-sm transition-shadow">
                                                <div className="col-span-4">
                                                    <button 
                                                        type="button"
                                                        onClick={() => toggleAllInModule(item.baseKey)}
                                                        className="text-sm font-medium text-gray-700 hover:text-blue-600 text-left w-full"
                                                    >
                                                        {item.label}
                                                    </button>
                                                </div>
                                                {CRUD_ACTIONS.map(action => {
                                                    const permKey = `${item.baseKey}_${action.key}`;
                                                    const isChecked = editingRole.permissions.includes(permKey);
                                                    return (
                                                        <div key={permKey} className="col-span-2 flex items-center justify-between md:justify-center mt-2 md:mt-0">
                                                            <span className="md:hidden text-xs text-gray-500 mr-2">{action.label}</span>
                                                            <input 
                                                                type="checkbox"
                                                                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
                                                                checked={isChecked}
                                                                onChange={() => togglePermission(permKey)}
                                                            />
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    } else {
                                        const isChecked = editingRole.permissions.includes(item.baseKey);
                                        return (
                                            <div key={item.baseKey} className="flex items-center justify-between p-2 bg-white rounded border border-gray-100">
                                                <span className="text-sm font-medium text-gray-700 pl-2">{item.label}</span>
                                                <label className="relative inline-flex items-center cursor-pointer mr-2">
                                                    <input 
                                                        type="checkbox" 
                                                        className="sr-only peer"
                                                        checked={isChecked}
                                                        onChange={() => togglePermission(item.baseKey)}
                                                    />
                                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                                </label>
                                            </div>
                                        );
                                    }
                                })}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="flex gap-3 pt-4 border-t">
                    <button onClick={handleSave} className="px-6 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 font-semibold flex items-center gap-2">
                        <Save size={18} /> Guardar
                    </button>
                    <button onClick={handleCancel} className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 font-semibold">
                        Cancelar
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div>
            <button onClick={() => handleEdit({ name: '', permissions: [] })} className="w-full px-4 py-3 mb-6 font-semibold text-white bg-green-600 rounded-md hover:bg-green-700 flex items-center justify-center gap-2">
                <Users size={20} /> Criar Nova Função
            </button>
            <div className="space-y-3">
                {roles.map(role => (
                    <div key={role.id} className="flex items-center justify-between p-4 bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-100 rounded-full">
                                <Lock size={20} className="text-blue-600" />
                            </div>
                            <span className="font-semibold text-gray-800">{role.name}</span>
                        </div>
                        <div className="flex gap-2">
                            {(() => {
                                const canEdit = role.name !== 'Admin' && (currentUser.is_super_user || role.name !== 'Padrão');
                                const canDelete = role.name !== 'Admin' && role.name !== 'Padrão';

                                return (
                                    <>
                                        {canEdit && (
                                            <button onClick={() => handleEdit(role)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-full" title="Editar">
                                                <Edit size={20}/>
                                            </button>
                                        )}
                                        {canDelete && (
                                            <button onClick={() => handleDelete(role.id, role.name)} className="p-2 text-red-600 hover:bg-red-50 rounded-full" title="Excluir">
                                                <Trash2 size={20}/>
                                            </button>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const UserManagement = ({ onDataChange, onEditUser, onEditEntity, currentUser, allCurrencies }) => {
    const [view, setView] = useState('users'); 
    const [refreshKey, setRefreshKey] = useState(0); 
    
    const handleDataChange = () => {
        setRefreshKey(prev => prev + 1);
        if(onDataChange) onDataChange();
    };

    return (
        <div>
            <div className="flex mb-6 border-b">
                <button onClick={() => setView('users')} className={`px-6 py-3 font-semibold transition-colors ${view === 'users' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>Utilizadores</button>
                <button onClick={() => setView('roles')} className={`px-6 py-3 font-semibold transition-colors ${view === 'roles' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>Funções</button>
                {currentUser.is_super_user && (
                    <button onClick={() => setView('entities')} className={`px-6 py-3 font-semibold transition-colors ${view === 'entities' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>Entidades</button>
                )}
            </div>
            {view === 'users' && <UsersManager key={`users-${refreshKey}`} onDataChange={handleDataChange} onEditUser={onEditUser} currentUser={currentUser} allCurrencies={allCurrencies} />}
            {view === 'roles' && <RolesManager key={`roles-${refreshKey}`} onDataChange={handleDataChange} currentUser={currentUser} />}
            {view === 'entities' && <EntitiesManager key={`entities-${refreshKey}`} onDataChange={handleDataChange} onEditEntity={onEditEntity} allCurrencies={allCurrencies} currentUser={currentUser} />}
        </div>
    );
};

const SettingsPage = ({ accountTypes, settings, onDataChange, permissions, onEditUser, onEditEntity, currentUser, allCurrencies }) => {
    const [openSection, setOpenSection] = useState('categories');

    return (
        <div>
            <h1 className="text-3xl font-bold text-gray-800">Configurações</h1>
            <p className="mb-6 text-gray-600">Gira as suas preferências e personalizações.</p>

            <div className="space-y-4">
                 <CollapsibleSection 
                    title="Geral" 
                    icon={Sliders}
                    isOpen={openSection === 'general'}
                    onClick={() => setOpenSection(openSection === 'general' ? null : 'general')}
                >
                    <GeneralSettingsManager settings={settings} onDataChange={onDataChange} />
                </CollapsibleSection>

                 <CollapsibleSection 
                    title="Categorias" 
                    icon={CalendarIcon}
                    isOpen={openSection === 'categories'}
                    onClick={() => setOpenSection(openSection === 'categories' ? null : 'categories')}
                >
                    <CategoriesManager onDataChange={onDataChange} />
                </CollapsibleSection>

                <CollapsibleSection 
                    title="Tipos de Conta" 
                    icon={CreditCard}
                    isOpen={openSection === 'types'}
                    onClick={() => setOpenSection(openSection === 'types' ? null : 'types')}
                >
                    <AccountTypesManager accountTypes={accountTypes} onDataChange={onDataChange} />
                </CollapsibleSection>

                <CollapsibleSection 
                    title="Chaves de API" 
                    icon={KeyRound}
                    isOpen={openSection === 'api'}
                    onClick={() => setOpenSection(openSection === 'api' ? null : 'api')}
                >
                    <ApiKeysManager settings={settings} onDataChange={onDataChange} />
                </CollapsibleSection>

                {(permissions.manageUsers || permissions.settings_edit || permissions.users_view || permissions.settings_users_view) && (
                    <CollapsibleSection 
                        title="Gestão de Entidades e Utilizadores" 
                        icon={Users}
                        isOpen={openSection === 'users'}
                        onClick={() => setOpenSection(openSection === 'users' ? null : 'users')}
                    >
                        <UserManagement onDataChange={onDataChange} onEditUser={onEditUser} onEditEntity={onEditEntity} currentUser={currentUser} allCurrencies={allCurrencies} />
                    </CollapsibleSection>
                )}
            </div>
        </div>
    );
};

export default SettingsPage;